import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import BasicTitle from "../components/title";
import Loader from "./loader";

import Api from "../libs/api";
import Storage from "../libs/storage";
import ErrorModal from "./error_modal";
import GenericModal from "./generic_modal";
import PermissionMultiSelect from "./permission_multiselect";

const ModalCenter = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    max-width: 688px;
    background-color: #ffffff;
    padding: 40px;
    border-radius: 8px;
    max-height: calc(100vh - 24px);
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid rgb(144, 144, 144);
    -webkit-box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.75);
    -moz-box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.75);
    box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.75);

    @media (max-width: 768px) {
        max-height: calc(100vh - 12px);
    }
`;

const ModalClose = styled.img`
    position: absolute;
    right: 20px;
    top: 20px;
    cursor: pointer;
`;

const ModalView = styled.div`
    margin-bottom: 16px;
`;

const ModalLineView = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const ModalFooter = styled.div`
    display: flex;
    width: 100%;
    align-items: right;
    justify-content: flex-end;
`;

const Clear = styled.div`
    clear: both;
`;

export default function EditUser({
    user,
    title,
    open,
    close,
    banks,
    categories,
    projects,
    getUsers,
    impersonateCompanyId,
    company,
}) {
    let api = Api();
    let storage = Storage();

    const [loading, setLoading] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState("");

    function showErrorModal(message) {
        setErrorModalMessage(message);
        setErrorModalOpen(true);
    }

    const [selectedProjectsInput, setSelectedProjectsInput] = useState([]);
    const [selectedAdminProjectsInput, setSelectedAdminProjectsInput] =
        useState([]);

    const [selectedCategoriesInput, setSelectedCategoriesInput] = useState([]);
    const [selectedAdminCategoriesInput, setSelectedAdminCategoriesInput] =
        useState([]);

    const activeProjectsSorted = projects
        .filter((project) => !project.disabled)
        .sort((a, b) => a.name.localeCompare(b.name));

    const activeCategoriesSorted = categories
        .filter((category) => !category.disabled)
        .sort((a, b) => a.name.localeCompare(b.name));


    const [name, setName] = useState(user.name);
    const [email, setEmail] = useState(user.email);
    const [role, setRole] = useState(user.role);
    const [hiringType, setHiringType] = useState(user.userHiringType);
    const [document, setDocument] = useState(user.cpf ? user.cpf : user.cnpj);
    const [bank, setBank] = useState(user.bankCode);
    const [agency, setAgency] = useState(user.bankAgency);
    const [account, setAccount] = useState(user.bankAccount);
    const [pixType, setPixType] = useState(user.pixType);
    const [pixValue, setPixValue] = useState(user.pix);
    const [valueTab, setValueTab] = React.useState("1");

    const [companySettings, setCompanySettings] = useState(
        user.userPermissions.companySettings,
    );
    const [userSettings, setUserSettings] = useState(
        user.userPermissions.users,
    );
    const [plansSettings, setPlansSettings] = useState(
        user.userPermissions.plans,
    );
    const [invoicesSettings, setInvoicesSettings] = useState(
        user.userPermissions.invoices,
    );
    const [creditsSettings, setCreditsSettings] = useState(
        user.userPermissions.credits,
    );
    const [refundPay, setRefundPay] = useState(user.userPermissions.refundPay);
    const [refundApprove, setRefundApprove] = useState(
        user.userPermissions.refundApprove,
    );
    const [genericModalOpen, setGenericModalOpen] = useState(false);

    useEffect(() => {
        let _categories = [];
        for (
            let index = 0;
            index < user?.restrictedCategories?.length;
            index++
        ) {
            const _category = user.restrictedCategories[index];
            _categories.push(btoa(`CategoryNodeView:${_category.id}`));
        }
        const categoryIds = _categories.map((id) => id);

        let filteredCategories = categories.filter(
            (category) =>
                !categoryIds.includes(category.id) && !category.disabled,
        );

        if (filteredCategories.length === 0) {
            filteredCategories = categories.filter(
                (category) => !category.disabled,
            );
        }

        const filteredCategoryIds = filteredCategories.map(
            (category) => category.id,
        );
        setSelectedCategoriesInput(filteredCategoryIds);

        _categories = [];
        for (
            let index = 0;
            index < user?.userPermissions?.categories?.length;
            index++
        ) {
            const _categoryId = user.userPermissions.categories[index];
            _categories.push(btoa(`CategoryNodeView:${_categoryId}`));
        }

        let filteredAdminCategories;
        if (user?.userPermissions?.categories?.length === 0) {
            filteredAdminCategories = categories.filter(
                (category) => !category.disabled,
            );
        } else {
            filteredAdminCategories = categories.filter((category) => {
                return _categories.includes(category.id) && !category.disabled;
            });
        }

        const filteredAdminCategoryIds = filteredAdminCategories.map(
            (category) => category.id,
        );

        setSelectedAdminCategoriesInput(filteredAdminCategoryIds);

        let _projects = [];
        for (let index = 0; index < user?.restrictedProjects?.length; index++) {
            const _project = user.restrictedProjects[index];
            _projects.push(btoa(`ProjectNodeView:${_project.id}`));
        }

        const projectIds = _projects.map((id) => id);

        let filteredProjects = projects.filter(
            (project) => !projectIds.includes(project.id) && !project.disabled,
        );

        if (filteredProjects.length === 0) {
            filteredProjects = projects.filter((project) => !project.disabled);
        }

        const filteredProjectIds = filteredProjects.map(
            (project) => project.id,
        );
        setSelectedProjectsInput(filteredProjectIds);

        _projects = [];
        for (
            let index = 0;
            index < user?.userPermissions?.projects?.length;
            index++
        ) {
            const _projectId = user.userPermissions.projects[index];
            _projects.push(btoa(`ProjectNodeView:${_projectId}`));
        }

        let filteredAdminProjects;
        if (user?.userPermissions?.projects?.length === 0) {
            filteredAdminProjects = projects.filter(
                (project) => !project.disabled,
            );
        } else {
            filteredAdminProjects = projects.filter((project) => {
                return _projects.includes(project.id) && !project.disabled;
            });
        }

        const filteredAdminProjectsIds = filteredAdminProjects.map(
            (project) => project.id,
        );

        setSelectedAdminProjectsInput(filteredAdminProjectsIds);

        setName(user.name);
        setEmail(user.email);
        setRole(user.role);
        setHiringType(user.userHiringType);
        setDocument(user.cpf ? user.cpf : user.cnpj);
        setBank(user.bankCode);
        setAgency(user.bankAgency);
        setAccount(user.bankAccount);
        setPixType(user.pixType);
        setPixValue(user.pix);

        setCompanySettings(user.userPermissions.companySettings);
        setUserSettings(user.userPermissions.users);
        setCreditsSettings(user.userPermissions.credits);
        setPlansSettings(user.userPermissions.plans);
        setInvoicesSettings(user.userPermissions.invoices);
        setRefundPay(user.userPermissions.refundPay);
        setRefundApprove(user.userPermissions.refundApprove);

        setValueTab("1");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const saveUser = async () => {
        if (name) {
            setLoading(true);
            try {
                const data = await storage.getItem("data");

                const selectedProjects =
                    selectedProjectsInput.length === 0
                        ? []
                        : projects
                              .filter(
                                  (project) =>
                                      !selectedProjectsInput.includes(
                                          project.id,
                                      ),
                              )
                              .map((project) => project.id);

                const selectedCategories =
                    selectedCategoriesInput.length === 0
                        ? []
                        : categories
                              .filter(
                                  (category) =>
                                      !selectedCategoriesInput.includes(
                                          category.id,
                                      ),
                              )
                              .map((category) => category.id);

                const response = await api.saveAdminUser(
                    data.token,
                    name,
                    email,
                    role,
                    hiringType,
                    document,
                    bank,
                    agency,
                    account,
                    pixType,
                    pixValue,
                    companySettings,
                    userSettings,
                    creditsSettings,
                    plansSettings,
                    invoicesSettings,
                    refundPay,
                    refundApprove,
                    selectedCategories,
                    selectedProjects,
                    selectedAdminCategoriesInput,
                    selectedAdminProjectsInput,
                );

                setLoading(false);
                if (
                    response &&
                    response.errors &&
                    response.errors[0]?.extensions?.code ===
                        "LAST_OWNER_PROTECTED"
                ) {
                    setGenericModalOpen(true);
                    return; // Não fecha modal principal nem atualiza usuários
                }
                getUsers("all", "all");
                close();
            } catch (error) {
                setLoading(false);
                showErrorModal(
                    "Não foi possível SALVAR AS EDIÇÕES. Verifique os dados e tente novamente.",
                );
            }
        }
    };

    const handleChangeTab = (event, newValue) => {
        setValueTab(newValue);
    };

    const hasUserSelections =
        selectedProjectsInput.length > 0 && selectedCategoriesInput.length > 0;
    const hasAdminSelections =
        role > 0
            ? selectedAdminProjectsInput.length > 0 &&
              selectedAdminCategoriesInput.length > 0
            : true;
    const isSaveDisabled = !hasUserSelections || !hasAdminSelections;

    return (
        <>
            <Modal
                open={open}
                onClose={() => {
                    close();
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <ModalCenter style={{ width: 1280, maxWidth: "100%" }}>
                    <ModalClose
                        src="/img/modal_close.svg"
                        onClick={() => {
                            close();
                        }}
                    />
                    {loading && <Loader inside={true} />}
                    <BasicTitle title={title} margin={32} />
                    <Clear />
                    <ModalView>
                        <>
                            <Box sx={{ width: "100%", typography: "body1" }}>
                                <TabContext value={valueTab}>
                                    <Box
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: "divider",
                                        }}
                                    >
                                        <TabList
                                            onChange={handleChangeTab}
                                            aria-label="lab API tabs example"
                                        >
                                            <Tab
                                                label="Dados Pessoais"
                                                value="1"
                                            />
                                            <Tab label="Permissōes" value="3" />
                                            {user.role > 0 && (
                                                <Tab label="Admin" value="4" />
                                            )}
                                        </TabList>
                                    </Box>
                                    <TabPanel
                                        value="1"
                                        style={{ padding: "24px 0px 0px" }}
                                    >
                                        <ModalLineView>
                                            <TextField
                                                id="outlined-basic"
                                                label="Nome"
                                                value={name}
                                                variant="outlined"
                                                style={{
                                                    width: "90%",
                                                    marginRight: 24,
                                                }}
                                                onChange={(event) => {
                                                    setName(event.target.value);
                                                }}
                                            />
                                            <FormControl
                                                style={{
                                                    width: "90%",
                                                    marginRight: 24,
                                                }}
                                            >
                                                <InputLabel>Perfil</InputLabel>
                                                <Select
                                                    labelId="demo-simple-select-label"
                                                    id="demo-simple-select"
                                                    label="Perfil"
                                                    value={role}
                                                    defaultValue={role}
                                                    onChange={(event) => {
                                                        const newRole =
                                                            event.target.value;
                                                        setRole(newRole);
                                                        if (
                                                            role === 0 &&
                                                            (newRole === 1 ||
                                                                newRole === 2)
                                                        ) {
                                                            setCompanySettings(
                                                                true,
                                                            );
                                                            setUserSettings(
                                                                true,
                                                            );
                                                            setCreditsSettings(
                                                                true,
                                                            );
                                                            setInvoicesSettings(
                                                                true,
                                                            );
                                                            setRefundPay(true);
                                                            setRefundApprove(
                                                                true,
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <MenuItem key={0} value={0}>
                                                        Usuário
                                                    </MenuItem>
                                                    <MenuItem key={1} value={1}>
                                                        Administrador
                                                    </MenuItem>
                                                    <MenuItem key={2} value={2}>
                                                        Proprietário
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                id="outlined-basic"
                                                label="Email"
                                                value={email}
                                                disabled
                                                variant="outlined"
                                                style={{
                                                    width: "90%",
                                                    marginRight: 24,
                                                }}
                                            />
                                        </ModalLineView>
                                    </TabPanel>
                                    <TabPanel
                                        value="3"
                                        style={{ padding: "24px 0px" }}
                                    >
                                        <p>
                                            Selecione os centros de custos e
                                            categorias que deseja liberar para o
                                            usuário.
                                        </p>
                                        <ModalLineView
                                            style={{
                                                width: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                marginTop: 24,
                                            }}
                                        >
                                            <ModalLineView>
                                                <FormControl
                                                    style={{
                                                        width: "100%",
                                                        marginRight: 24,
                                                        display: "flex",
                                                        alignItems: "flex-end",
                                                        border: "1px solid transparent",
                                                    }}
                                                >
                                                    <Box
                                                        component="section"
                                                        sx={{
                                                            width: "100%",
                                                            display: "flex",
                                                            flexDirection:
                                                                "column",
                                                            alignItems:
                                                                "flex-start",
                                                            justifyContent:
                                                                "center",
                                                        }}
                                                    >
                                                        <PermissionMultiSelect
                                                            label="Centro de Custo"
                                                            options={
                                                                activeProjectsSorted
                                                            }
                                                            selectedIds={
                                                                selectedProjectsInput
                                                            }
                                                            onChangeSelectedIds={
                                                                setSelectedProjectsInput
                                                            }
                                                            searchPlaceholder="Buscar centro de custo"
                                                        />
                                                    </Box>
                                                </FormControl>
                                                <FormControl
                                                    style={{
                                                        width: "100%",
                                                        marginRight: 24,
                                                        display: "flex",
                                                        alignItems: "flex-end",
                                                        border: "1px solid transparent",
                                                    }}
                                                >
                                                    <Box
                                                        component="section"
                                                        sx={{
                                                            width: "100%",
                                                            display: "flex",
                                                            flexDirection:
                                                                "column",
                                                            alignItems:
                                                                "flex-start",
                                                            justifyContent:
                                                                "center",
                                                        }}
                                                    >
                                                        <PermissionMultiSelect
                                                            label="Categorias"
                                                            options={
                                                                activeCategoriesSorted
                                                            }
                                                            selectedIds={
                                                                selectedCategoriesInput
                                                            }
                                                            onChangeSelectedIds={
                                                                setSelectedCategoriesInput
                                                            }
                                                            searchPlaceholder="Buscar categoria"
                                                        />
                                                    </Box>
                                                </FormControl>
                                            </ModalLineView>
                                        </ModalLineView>
                                    </TabPanel>
                                    {user.role > 0 && (
                                        <TabPanel
                                            value="4"
                                            style={{ padding: "24px 0px" }}
                                        >
                                            <ModalLineView>
                                                <FormGroup
                                                    style={{ width: "100%" }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    companySettings
                                                                }
                                                                onChange={(
                                                                    event,
                                                                ) => {
                                                                    setCompanySettings(
                                                                        event
                                                                            .target
                                                                            .checked,
                                                                    );
                                                                }}
                                                                inputProps={{
                                                                    "aria-label":
                                                                        "controlled",
                                                                }}
                                                            />
                                                        }
                                                        label="Configurações Empresa"
                                                    />
                                                </FormGroup>
                                                <FormGroup
                                                    style={{ width: "100%" }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    userSettings
                                                                }
                                                                onChange={(
                                                                    event,
                                                                ) => {
                                                                    setUserSettings(
                                                                        event
                                                                            .target
                                                                            .checked,
                                                                    );
                                                                }}
                                                                inputProps={{
                                                                    "aria-label":
                                                                        "controlled",
                                                                }}
                                                            />
                                                        }
                                                        label="Configurações Usuários"
                                                    />
                                                </FormGroup>
                                                <FormGroup
                                                    style={{ width: "100%" }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    creditsSettings
                                                                }
                                                                onChange={(
                                                                    event,
                                                                ) => {
                                                                    setCreditsSettings(
                                                                        event
                                                                            .target
                                                                            .checked,
                                                                    );
                                                                }}
                                                                inputProps={{
                                                                    "aria-label":
                                                                        "controlled",
                                                                }}
                                                            />
                                                        }
                                                        label="Gerenciar Créditos"
                                                    />
                                                </FormGroup>
                                                {/* {company.enablePayments ? (
                                                    <FormGroup
                                                        style={{
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={
                                                                        plansSettings
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                    ) => {
                                                                        setPlansSettings(
                                                                            event
                                                                                .target
                                                                                .checked,
                                                                        );
                                                                    }}
                                                                    inputProps={{
                                                                        "aria-label":
                                                                            "controlled",
                                                                    }}
                                                                />
                                                            }
                                                            label="Gerenciar Planos"
                                                        />
                                                    </FormGroup>
                                                ) : (
                                                    <FormGroup
                                                        style={{
                                                            width: "100%",
                                                        }}
                                                    />
                                                )} */}

                                            </ModalLineView>
                                            <ModalLineView>
                                                <FormGroup
                                                    style={{ width: "100%" }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    refundPay
                                                                }
                                                                onChange={(
                                                                    event,
                                                                ) => {
                                                                    setRefundPay(
                                                                        event
                                                                            .target
                                                                            .checked,
                                                                    );
                                                                }}
                                                                inputProps={{
                                                                    "aria-label":
                                                                        "controlled",
                                                                }}
                                                            />
                                                        }
                                                        label="Pagar Reembolsos"
                                                    />
                                                </FormGroup>
                                                {/* {company.enablePayments &&
                                                company.hasActiveSubscription ? (
                                                    <FormGroup
                                                        style={{
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={
                                                                        invoicesSettings
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                    ) => {
                                                                        setInvoicesSettings(
                                                                            event
                                                                                .target
                                                                                .checked,
                                                                        );
                                                                    }}
                                                                    inputProps={{
                                                                        "aria-label":
                                                                            "controlled",
                                                                    }}
                                                                />
                                                            }
                                                            label="Gerenciar Faturas"
                                                        />
                                                    </FormGroup>
                                                ) : (
                                                    <FormGroup
                                                        style={{
                                                            width: "100%",
                                                        }}
                                                    />
                                                )} */}
                                                <FormGroup
                                                    style={{ width: "100%" }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    refundApprove
                                                                }
                                                                onChange={(
                                                                    event,
                                                                ) => {
                                                                    setRefundApprove(
                                                                        event
                                                                            .target
                                                                            .checked,
                                                                    );
                                                                }}
                                                                inputProps={{
                                                                    "aria-label":
                                                                        "controlled",
                                                                }}
                                                            />
                                                        }
                                                        label="Aprovar Reembolsos"
                                                    />
                                                </FormGroup>
                                                <FormGroup style={{ width: "100%" }} />
                                            </ModalLineView>
                                            <p style={{ marginTop: "24px" }}>
                                                Selecione os centros de custos e
                                                categorias que deseja liberar
                                                para o usuário gerenciar.
                                            </p>
                                            <ModalLineView
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-start",
                                                    marginTop: 24,
                                                }}
                                            >
                                                <ModalLineView>
                                                    <FormControl
                                                        style={{
                                                            width: "100%",
                                                            marginRight: 24,
                                                            display: "flex",
                                                            alignItems:
                                                                "flex-end",
                                                        }}
                                                    >
                                                        <Box
                                                            component="section"
                                                            sx={{
                                                                width: "100%",
                                                                display: "flex",
                                                                flexDirection:
                                                                    "column",
                                                                alignItems:
                                                                    "flex-start",
                                                                justifyContent:
                                                                    "center",
                                                            }}
                                                        >
                                                            <PermissionMultiSelect
                                                                label="Centro de Custo Gerenciaveis"
                                                                options={
                                                                    activeProjectsSorted
                                                                }
                                                                selectedIds={
                                                                    selectedAdminProjectsInput
                                                                }
                                                                onChangeSelectedIds={
                                                                    setSelectedAdminProjectsInput
                                                                }
                                                                searchPlaceholder="Buscar centro de custo"
                                                            />
                                                        </Box>
                                                    </FormControl>
                                                    <FormControl
                                                        style={{
                                                            width: "100%",
                                                            display: "flex",
                                                            alignItems:
                                                                "flex-end",
                                                        }}
                                                    >
                                                        <Box
                                                            component="section"
                                                            sx={{
                                                                width: "100%",
                                                                display: "flex",
                                                                flexDirection:
                                                                    "column",
                                                                alignItems:
                                                                    "flex-start",
                                                                justifyContent:
                                                                    "center",
                                                                minHeight:
                                                                    "56px",
                                                            }}
                                                        >
                                                            <PermissionMultiSelect
                                                                label="Categorias Gerenciaveis"
                                                                options={
                                                                    activeCategoriesSorted
                                                                }
                                                                selectedIds={
                                                                    selectedAdminCategoriesInput
                                                                }
                                                                onChangeSelectedIds={
                                                                    setSelectedAdminCategoriesInput
                                                                }
                                                                searchPlaceholder="Buscar categoria"
                                                            />
                                                        </Box>
                                                    </FormControl>
                                                </ModalLineView>
                                            </ModalLineView>
                                        </TabPanel>
                                    )}
                                </TabContext>
                            </Box>
                        </>
                    </ModalView>
                    <Clear />
                    <ModalFooter>
                        {impersonateCompanyId === null ? (
                            <Tooltip
                                title={
                                    isSaveDisabled
                                        ? "Selecione ao menos um Centro de Custo e uma Categoria"
                                        : "Salvar"
                                }
                            >
                                <span
                                    style={{
                                        cursor: isSaveDisabled
                                            ? "not-allowed"
                                            : "pointer",
                                    }}
                                >
                                    <Button
                                        style={{
                                            width: 150,
                                            fontSize: 12,
                                            padding: "8px 0px",
                                        }}
                                        variant={"contained"}
                                        color={"primary"}
                                        disabled={isSaveDisabled}
                                        onClick={saveUser}
                                    >
                                        SALVAR
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : (
                            <Tooltip
                                title={
                                    "Não é possível editar um usuário via Impersonar"
                                }
                            >
                                <span style={{ cursor: "not-allowed" }}>
                                    <Button
                                        style={{
                                            width: 150,
                                            fontSize: 12,
                                            padding: "8px 0px",
                                        }}
                                        variant={"outlined"}
                                        color={"primary"}
                                        disabled
                                    >
                                        Salvar
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </ModalFooter>
                </ModalCenter>
            </Modal>
            <ErrorModal
                open={errorModalOpen}
                onClose={() => setErrorModalOpen(false)}
                message={errorModalMessage}
            />
            <GenericModal
                width={900}
                open={genericModalOpen}
                onClose={() => setGenericModalOpen(false)}
                title="ALTERAÇÃO NÃO PERMITIDA"
                subtitle={
                    <>
                        Cada empresa precisa ter pelo menos um proprietário
                        cadastrado.
                        <br />
                        Você não pode mudar seu perfil porque, no momento, você
                        é o único proprietário da empresa.
                    </>
                }
                buttonText="Entendi"
                onButtonClick={() => setGenericModalOpen(false)}
            />
        </>
    );
}
